import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type SimulationRun } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';
import type { SimulationRunRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const SIMULATION_RUNS = 'simulationRuns';

const converter = buildConverter<SimulationRun>(
  (data) =>
    ({
      ...data,
      startedAt: toTimestampString(data.startedAt) ?? new Date().toISOString(),
      endedAt: toTimestampString(data.endedAt) ?? undefined,
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
      updatedAt: toTimestampString(data.updatedAt) ?? new Date().toISOString(),
    }) as SimulationRun,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreSimulationRunRepository implements SimulationRunRepository {
  async findAll(): Promise<SimulationRun[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, SIMULATION_RUNS),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('startedAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<SimulationRun | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, SIMULATION_RUNS, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<SimulationRun[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, SIMULATION_RUNS),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('startedAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findBySimulationTestId(simulationTestId: string): Promise<SimulationRun[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, SIMULATION_RUNS),
      where('simulationTestId', '==', simulationTestId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('startedAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(run: SimulationRun): Promise<SimulationRun> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const data = { ...run, environment: getCurrentEnvironment() };
    const existing = await this.findById(run.id);
    await setDoc(doc(db, SIMULATION_RUNS, run.id).withConverter(converter), data);
    return {
      ...data,
      createdAt: existing?.createdAt ?? run.createdAt,
      updatedAt: new Date().toISOString(),
    };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, SIMULATION_RUNS, id));
  }
}

export const createFirestoreSimulationRunRepository = (): FirestoreSimulationRunRepository =>
  new FirestoreSimulationRunRepository();
